import type { AnalysisResult, Medicine, PatientInfo } from '../types';
import { analyzeVerifiedMedicines } from './openRouterService';
import { loadAnalysisResult, saveAnalysisResult, saveReviewDraft } from './analysisStorage';

type TimeSlot = 'morning' | 'noon' | 'evening' | 'night';
type ProviderName = 'gemini' | 'openrouter';

interface AiPatient {
  name?: string;
  age?: string;
  date?: string;
  doctorName?: string;
  hospitalName?: string;
}

interface AiMedicine {
  brandName?: string;
  genericName?: string;
  form?: string;
  strength?: string;
  dose?: string;
  frequencyPattern?: string;
  timeSlots?: string[];
  foodTiming?: string;
  duration?: string;
  englishInstruction?: string;
  banglaInstruction?: string;
  confidence?: number;
  needsReview?: boolean;
  reviewReason?: string;
}

interface AiPrescriptionPayload {
  patient?: AiPatient;
  medicines?: AiMedicine[];
  possibleContext?: string[];
  warnings?: string[];
  rawText?: string;
}

interface ProviderAttempt {
  provider: ProviderName;
  model?: string;
}

interface OpenRouterModelInfo {
  id?: string;
  architecture?: {
    input_modalities?: string[];
    output_modalities?: string[];
    modality?: string;
  };
}

export type AiProviderStatus = 'completed' | 'cached' | 'review';

export interface AiProviderResult {
  status: AiProviderStatus;
  imageHash: string;
  analysis?: AnalysisResult;
  draftMedicines?: Medicine[];
  ocrText?: string;
  message?: string;
}

const GEMINI_API_BASE = 'https://generativelanguage.googleapis.com/v1beta/models';
const OPENROUTER_ENDPOINT = 'https://openrouter.ai/api/v1/chat/completions';
const OPENROUTER_MODELS_ENDPOINT = 'https://openrouter.ai/api/v1/models';
const GEMINI_API_KEY = (import.meta.env.GEMINI_API_KEY || import.meta.env.VITE_GEMINI_API_KEY || '').trim();
const OPENROUTER_API_KEY = (import.meta.env.OPENROUTER_API_KEY || import.meta.env.VITE_OPENROUTER_API_KEY || '').trim();
const PROVIDER_COOLDOWN_KEY = 'medcheck_ai_provider_cooldowns_v1';
const GLOBAL_COOLDOWN_KEY = 'medcheck_ai_global_cooldown_v1';
const LAST_ANALYSIS_IMAGE_HASH_KEY = 'medcheck_ai_last_analysis_image_hash_v1';
const OPENROUTER_MODEL_CACHE_KEY = 'medcheck_openrouter_current_vision_models_v1';
const STRUCTURED_CACHE_PREFIX = 'medcheck_structured_payload_';
const REQUEST_TIMEOUT_MS = 45 * 1000;
const PROVIDER_COOLDOWN_MS = 5 * 60 * 1000;
const GLOBAL_COOLDOWN_MS = 3 * 60 * 1000;
const OPENROUTER_MODEL_CACHE_MS = 6 * 60 * 60 * 1000;
const inFlightByImageHash = new Map<string, Promise<AiProviderResult>>();

const GEMINI_MODELS = [
  'gemini-2.5-flash',
  'gemini-flash-latest',
  'gemini-3.5-flash',
];

const OPENROUTER_MODEL_PREFERENCES = [
  'qwen/qwen2.5-vl-32b-instruct:free',
  'qwen/qwen2.5-vl-7b-instruct:free',
  'qwen/qwen-2.5-vl-7b-instruct:free',
  'google/gemini-2.0-flash-exp:free',
];

const STRICT_SCHEMA_PROMPT = `Extract this prescription into strict JSON only.
Schema:
{"patient":{"name":"","age":"","date":"","doctorName":"","hospitalName":""},"medicines":[{"brandName":"","genericName":"","form":"","strength":"","dose":"","frequencyPattern":"","timeSlots":["morning","noon","evening","night"],"foodTiming":"","duration":"","englishInstruction":"","banglaInstruction":"","confidence":0,"needsReview":false,"reviewReason":""}],"possibleContext":[],"warnings":[],"rawText":""}
Rules: do not invent medicines, diagnoses, durations, or interactions. Preserve visible Bangla instructions. Convert 1+0+1 or Bangla digit schedules into timeSlots. Missing fields must be "Not specified". Return JSON only.`;

function devLog(message: string, details?: unknown) {
  if (!import.meta.env.DEV) return;
  if (details === undefined) {
    console.info(`[MedCheck AI] ${message}`);
    return;
  }
  console.info(`[MedCheck AI] ${message}`, details);
}

function getBase64Payload(imageBase64: string): string {
  return imageBase64.includes(',') ? imageBase64.split(',').pop() || '' : imageBase64;
}

function base64ToBytes(base64: string): Uint8Array {
  const cleanBase64 = base64.replace(/\s/g, '');
  const binary = window.atob(cleanBase64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await window.crypto.subtle.digest('SHA-256', bytes.buffer.slice(0) as ArrayBuffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

async function sha256Text(text: string): Promise<string> {
  return sha256Hex(new TextEncoder().encode(text));
}

export async function getPrescriptionImageHash(imageBase64: string): Promise<string> {
  return sha256Hex(base64ToBytes(getBase64Payload(imageBase64)));
}

function getProviderKey(attempt: ProviderAttempt) {
  return attempt.model ? `${attempt.provider}:${attempt.model}` : attempt.provider;
}

function readCooldowns(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(PROVIDER_COOLDOWN_KEY) || '{}') as Record<string, number>;
  } catch {
    return {};
  }
}

function writeCooldowns(cooldowns: Record<string, number>) {
  localStorage.setItem(PROVIDER_COOLDOWN_KEY, JSON.stringify(cooldowns));
}

function clearProviderCooldowns() {
  localStorage.removeItem(PROVIDER_COOLDOWN_KEY);
  localStorage.removeItem(GLOBAL_COOLDOWN_KEY);
}

function resetCooldownsForNewImage(imageHash: string) {
  const previousHash = sessionStorage.getItem(LAST_ANALYSIS_IMAGE_HASH_KEY) || '';
  sessionStorage.setItem(LAST_ANALYSIS_IMAGE_HASH_KEY, imageHash);

  if (previousHash !== imageHash) {
    clearProviderCooldowns();
    devLog('Provider cooldowns reset for new prescription image', { imageHash });
  }
}

function isBlocked(attempt: ProviderAttempt) {
  const cooldowns = readCooldowns();
  return (cooldowns[getProviderKey(attempt)] || 0) > Date.now();
}

function blockAttempt(attempt: ProviderAttempt, durationMs: number) {
  const cooldowns = readCooldowns();
  cooldowns[getProviderKey(attempt)] = Date.now() + durationMs;
  writeCooldowns(cooldowns);
}

function setGlobalCooldown() {
  localStorage.setItem(GLOBAL_COOLDOWN_KEY, String(Date.now() + GLOBAL_COOLDOWN_MS));
}

function isGlobalCoolingDown() {
  return Number(localStorage.getItem(GLOBAL_COOLDOWN_KEY) || 0) > Date.now();
}

function getRetryAfterMs(response: Response) {
  const retryAfter = response.headers.get('retry-after');
  if (!retryAfter) return PROVIDER_COOLDOWN_MS;

  const seconds = Number(retryAfter);
  if (Number.isFinite(seconds) && seconds > 0) return seconds * 1000;

  const dateMs = Date.parse(retryAfter);
  if (Number.isFinite(dateMs) && dateMs > Date.now()) return dateMs - Date.now();

  return PROVIDER_COOLDOWN_MS;
}

function readOpenRouterModelCache(): string[] | null {
  try {
    const cached = JSON.parse(localStorage.getItem(OPENROUTER_MODEL_CACHE_KEY) || 'null') as {
      timestamp: number;
      models: string[];
    } | null;
    if (!cached || Date.now() - cached.timestamp > OPENROUTER_MODEL_CACHE_MS) return null;
    return Array.isArray(cached.models) ? cached.models : null;
  } catch {
    return null;
  }
}

function writeOpenRouterModelCache(models: string[]) {
  try {
    localStorage.setItem(OPENROUTER_MODEL_CACHE_KEY, JSON.stringify({ timestamp: Date.now(), models }));
  } catch {
    // Model discovery cache is optional.
  }
}

function supportsVision(model: OpenRouterModelInfo) {
  const text = [
    model.id || '',
    model.architecture?.modality || '',
    ...(model.architecture?.input_modalities || []),
    ...(model.architecture?.output_modalities || []),
  ].join(' ').toLowerCase();

  return text.includes('image') || text.includes('vision') || text.includes('vl');
}

async function resolveOpenRouterModels(): Promise<string[]> {
  const cached = readOpenRouterModelCache();
  if (cached?.length) return cached;

  try {
    const response = await fetch(OPENROUTER_MODELS_ENDPOINT, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Model discovery failed');
    const data = await response.json();
    const availableModels = Array.isArray(data?.data) ? data.data as OpenRouterModelInfo[] : [];
    const availableIds = new Set(
      availableModels
        .filter((model) => model.id && supportsVision(model))
        .map((model) => model.id as string)
    );
    const models = OPENROUTER_MODEL_PREFERENCES.filter((model) => availableIds.has(model)).slice(0, 2);
    writeOpenRouterModelCache(models);
    return models;
  } catch {
    return OPENROUTER_MODEL_PREFERENCES.slice(0, 1);
  }
}

async function getProviderOrder(): Promise<ProviderAttempt[]> {
  const openRouterModels = await resolveOpenRouterModels();
  return [
    ...GEMINI_MODELS.map((model) => ({ provider: 'gemini' as const, model })),
    ...openRouterModels.slice(0, 2).map((model) => ({ provider: 'openrouter' as const, model })),
  ];
}

async function withTimeout<T>(task: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    return await task(controller.signal);
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function stripCodeFences(content: string) {
  return content
    .replace(/```json\s*/gi, '')
    .replace(/```\s*/gi, '')
    .replace(/[“”]/g, '"')
    .trim();
}

function parseStructuredPayload(content: string): AiPrescriptionPayload | null {
  const clean = stripCodeFences(content);
  const objectMatch = clean.match(/\{[\s\S]*\}/);
  if (!objectMatch) return null;

  const candidate = objectMatch[0].replace(/,\s*([}\]])/g, '$1');
  try {
    const parsed = JSON.parse(candidate) as AiPrescriptionPayload;
    return validateStructuredPayload(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function normalizeBanglaDigits(text: string): string {
  return text.replace(/[\u09E6-\u09EF]/g, (digit) => String(digit.charCodeAt(0) - 0x09E6));
}

function inferTimeSlots(pattern: string, providedSlots?: string[]): TimeSlot[] {
  const slots = new Set<TimeSlot>();
  const normalizedPattern = normalizeBanglaDigits(pattern || '').toLowerCase();
  const threePart = normalizedPattern.match(/([0-9]+)\s*\+\s*([0-9]+)\s*\+\s*([0-9]+)/);

  if (threePart) {
    if (Number(threePart[1]) > 0) slots.add('morning');
    if (Number(threePart[2]) > 0) slots.add('noon');
    if (Number(threePart[3]) > 0) slots.add('night');
  }

  (providedSlots || []).forEach((slot) => {
    const normalizedSlot = slot.toLowerCase();
    if (['morning', 'noon', 'evening', 'night'].includes(normalizedSlot)) {
      slots.add(normalizedSlot as TimeSlot);
    }
  });

  if (/morning|সকাল/.test(normalizedPattern)) slots.add('morning');
  if (/noon|দুপুর/.test(normalizedPattern)) slots.add('noon');
  if (/evening|সন্ধ্যা|বিকাল/.test(normalizedPattern)) slots.add('evening');
  if (/night|রাত/.test(normalizedPattern)) slots.add('night');

  return Array.from(slots);
}

function normalizeFoodTiming(foodTiming: string, frequencyPattern: string) {
  const text = `${foodTiming} ${frequencyPattern}`.toLowerCase();
  if (/before food|before meal|খাবার আগে|খাবারের আগে|পূর্বে/.test(text)) return 'Before food';
  if (/after food|after meal|খাবার পরে|খাবারের পরে|খাবার পর/.test(text)) return 'After food';
  return foodTiming || 'Not specified';
}

function validateStructuredPayload(payload: AiPrescriptionPayload) {
  if (!payload || typeof payload !== 'object') return false;
  if (!Array.isArray(payload.medicines)) return false;
  const rawText = typeof payload.rawText === 'string' ? payload.rawText : '';
  if (payload.medicines.length === 0 && /rx|tab|cap|inj|mg|ml|tablet|capsule/i.test(rawText)) return false;
  return payload.medicines.every((medicine) => typeof medicine?.brandName === 'string' || typeof medicine?.genericName === 'string');
}

function mapPatient(patient?: AiPatient): PatientInfo {
  return {
    patientName: patient?.name || undefined,
    age: patient?.age || undefined,
    date: patient?.date || undefined,
    doctorName: patient?.doctorName || undefined,
    hospitalName: patient?.hospitalName || undefined,
  };
}

function mapMedicines(payload: AiPrescriptionPayload): Medicine[] {
  return (payload.medicines || [])
    .map((medicine): Medicine => {
      const name = medicine.brandName || medicine.genericName || 'Needs review';
      const frequency = medicine.frequencyPattern || 'Not specified';
      const timeSlots = inferTimeSlots(frequency, medicine.timeSlots);
      const timing = normalizeFoodTiming(medicine.foodTiming || '', frequency);
      const reviewReason = medicine.reviewReason || (medicine.needsReview ? 'Confirm this medicine with the prescription or strip.' : '');

      return {
        name,
        generic_name: medicine.genericName || 'Needs verification',
        genericName: medicine.genericName || 'Needs verification',
        form: medicine.form || 'Not specified',
        strength: medicine.strength || 'Not specified',
        dosage: medicine.dose || 'Not specified',
        frequency,
        frequencyLabel: frequency,
        timing,
        timingLabel: timing,
        duration: medicine.duration || 'Not specified',
        durationLabel: medicine.duration || 'Not specified',
        instruction: medicine.englishInstruction || 'Confirm the instruction before use.',
        englishInstruction: medicine.englishInstruction || 'Confirm the instruction before use.',
        banglaInstruction: medicine.banglaInstruction || 'ব্যবহারের আগে নির্দেশনা নিশ্চিত করুন।',
        rawInstruction: [medicine.frequencyPattern, medicine.foodTiming, medicine.duration].filter(Boolean).join(' '),
        notes: reviewReason,
        confidence: Math.min(1, Math.max(0, medicine.confidence ?? 0.6)),
        verificationStatus: medicine.needsReview ? 'needs_verification' : 'known',
        identificationStatus: medicine.needsReview ? 'needs_review' : 'identified',
        genericConfirmed: Boolean(medicine.genericName && !medicine.needsReview),
        possibleMatches: [],
        isNebulization: /nebul/i.test(`${medicine.form} ${medicine.brandName}`),
        category: '',
        commonUse: '',
        riskNotes: '',
        confidenceSource: 'AI extraction',
        resolvedBrandName: name,
        timeSlots,
      } as Medicine;
    })
    .filter((medicine) => medicine.name.trim());
}

function getImageMimeType(imageBase64: string): string {
  return imageBase64.match(/^data:([^;]+);base64,/)?.[1] || 'image/jpeg';
}

async function callGemini(attempt: ProviderAttempt, imageBase64: string, signal: AbortSignal): Promise<Response> {
  if (!GEMINI_API_KEY || !attempt.model) {
    return new Response(JSON.stringify({ error: 'Gemini key missing' }), { status: 503 });
  }

  return fetch(`${GEMINI_API_BASE}/${attempt.model}:generateContent`, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      'x-goog-api-key': GEMINI_API_KEY,
    },
    body: JSON.stringify({
      contents: [{
        role: 'user',
        parts: [
          { text: STRICT_SCHEMA_PROMPT },
          {
            inline_data: {
              mime_type: getImageMimeType(imageBase64),
              data: getBase64Payload(imageBase64),
            },
          },
        ],
      }],
      generationConfig: {
        temperature: 0,
        response_mime_type: 'application/json',
      },
    }),
  });
}

async function callOpenRouter(attempt: ProviderAttempt, imageBase64: string, signal: AbortSignal): Promise<Response> {
  if (!OPENROUTER_API_KEY || !attempt.model) {
    return new Response(JSON.stringify({ error: 'OpenRouter key or model missing' }), { status: 503 });
  }

  return fetch(OPENROUTER_ENDPOINT, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${OPENROUTER_API_KEY}`,
      'HTTP-Referer': window.location.origin,
      'X-Title': 'MedCheck',
    },
    body: JSON.stringify({
      model: attempt.model,
      temperature: 0,
      max_tokens: 2600,
      messages: [{
        role: 'user',
        content: [
          { type: 'text', text: STRICT_SCHEMA_PROMPT },
          {
            type: 'image_url',
            image_url: {
              url: `data:${getImageMimeType(imageBase64)};base64,${getBase64Payload(imageBase64)}`,
            },
          },
        ],
      }],
    }),
  });
}

async function readProviderContent(attempt: ProviderAttempt, response: Response): Promise<string> {
  const data = await response.json().catch(() => ({}));
  if (attempt.provider === 'gemini') {
    return data.candidates?.[0]?.content?.parts?.map((part: { text?: string }) => part.text || '').join('') || '';
  }
  return data.choices?.[0]?.message?.content || '';
}

async function callProvider(attempt: ProviderAttempt, imageBase64: string, _imageHash: string): Promise<AiPrescriptionPayload | null> {
  devLog('Provider attempt started', { provider: attempt.provider, model: attempt.model || 'primary' });

  const response = await withTimeout((signal) =>
    attempt.provider === 'gemini'
      ? callGemini(attempt, imageBase64, signal)
      : callOpenRouter(attempt, imageBase64, signal)
  );

  if (response.status === 429) {
    blockAttempt(attempt, Math.max(PROVIDER_COOLDOWN_MS, getRetryAfterMs(response)));
    return null;
  }

  if (response.status === 404 && attempt.model) {
    blockAttempt(attempt, 24 * 60 * 60 * 1000);
    localStorage.removeItem(OPENROUTER_MODEL_CACHE_KEY);
    return null;
  }

  if (!response.ok) {
    blockAttempt(attempt, PROVIDER_COOLDOWN_MS);
    return null;
  }

  const content = await readProviderContent(attempt, response);
  return parseStructuredPayload(content);
}

async function runProviderPipeline(imageBase64: string, imageHash: string): Promise<AiProviderResult> {
  const cachedAnalysis = loadAnalysisResult(imageHash);
  if (cachedAnalysis) {
    devLog('Analysis cache hit', { imageHash });
    return { status: 'cached', imageHash, analysis: cachedAnalysis };
  }

  if (isGlobalCoolingDown()) {
    return {
      status: 'review',
      imageHash,
      draftMedicines: [],
      message: 'We could not read this prescription clearly. Please retake the photo or enter medicines manually.',
    };
  }

  let rateLimitCount = 0;
  const providerOrder = await getProviderOrder();

  for (const attempt of providerOrder) {
    if (isBlocked(attempt)) {
      devLog('Provider skipped due to cooldown', { provider: attempt.provider, model: attempt.model || 'primary' });
      continue;
    }

    const payload = await callProvider(attempt, imageBase64, imageHash).catch((error) => {
      devLog('Provider attempt failed', { provider: attempt.provider, model: attempt.model || 'primary', error });
      blockAttempt(attempt, PROVIDER_COOLDOWN_MS);
      return null;
    });

    if (!payload) {
      if (isBlocked(attempt)) rateLimitCount += 1;
      if (attempt.provider === 'openrouter' && rateLimitCount >= 2) break;
      continue;
    }

    const rawText = payload.rawText || '';
    const textHash = rawText ? await sha256Text(rawText) : '';
    if (textHash) {
      localStorage.setItem(`${STRUCTURED_CACHE_PREFIX}${textHash}`, JSON.stringify({ timestamp: Date.now(), value: payload }));
    }

    const medicines = mapMedicines(payload);
    if (medicines.length === 0) {
      continue;
    }

    const analysis = await analyzeVerifiedMedicines(medicines, rawText, {});
    const finalAnalysis: AnalysisResult = {
      ...analysis,
      ocrText: rawText,
      extractedText: rawText,
      patientInfo: mapPatient(payload.patient),
      warnings: [...(analysis.warnings || []), ...(payload.warnings || []).map((warning) => `Possible issue: ${warning}`)],
    };

    saveAnalysisResult(imageHash, finalAnalysis);
    return { status: 'completed', imageHash, analysis: finalAnalysis };
  }

  setGlobalCooldown();
  saveReviewDraft(imageHash, { imageHash, medicines: [], ocrText: '' });
  return {
    status: 'review',
    imageHash,
    draftMedicines: [],
    message: 'We could not read this prescription clearly. Please retake the photo or enter medicines manually.',
  };
}

export async function analyzePrescriptionWithAI(imageBase64: string): Promise<AiProviderResult> {
  const imageHash = await getPrescriptionImageHash(imageBase64);
  const existing = inFlightByImageHash.get(imageHash);
  if (existing) {
    devLog('Joined existing in-flight analysis', { imageHash });
    return existing;
  }

  resetCooldownsForNewImage(imageHash);
  const promise = runProviderPipeline(imageBase64, imageHash).finally(() => {
    inFlightByImageHash.delete(imageHash);
  });
  inFlightByImageHash.set(imageHash, promise);
  return promise;
}
